/*
 * The live data path for one run.
 *
 * Real mode (VITE_WEB_TEST_API_BASE_URL set): polls the verifier's resumable event
 * endpoint by afterSequence via api.getEvents, together with api.getRun,
 * merging through logic.mergeEvents / logic.mergeRunWithoutRegression so a
 * refreshed run can never regress a terminal verdict. Failures degrade
 * honestly: the last-known data stays on screen and the liveness state says
 * updates are broken.
 *
 * Fixture mode (no verifier configured): serves the committed fixtures and
 * simulates progression by revealing the run's fixture events over time so
 * the live view is demoable. The mode is always labelled — a simulated feed
 * never claims to be live. `localStorage.wtd-simulate = 'hold'` freezes the
 * simulation fully revealed (used by tests and the capture script).
 *
 * Pausing freezes what is rendered (run and events both) and buffers new
 * activity; resuming applies the buffer at once. Nothing here scrolls.
 */
import type { RunEvent, WebTestRun } from '@blume/web-test-model'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { createDashboardApi, DashboardApiError, type DashboardApi } from '@/api'
import { fixtureEvents, fixtureRuns } from '@/data'
import { mergeEvents, mergeRunWithoutRegression } from '@/logic'

import { applyEventsToRun } from './live-model'

export type FeedMode = 'fixture' | 'live'

export type FeedLiveness =
  | 'loading'
  | 'live'
  | 'paused'
  | 'degraded'
  | 'finished'
  | 'unauthorized'
  | 'not-found'

export interface RunFeed {
  mode: FeedMode
  liveness: FeedLiveness
  run: WebTestRun | undefined
  events: RunEvent[]
  /** True while progression is simulated from fixtures — never a real feed. */
  simulated: boolean
  /** True once this feed has ever shown the run in a non-terminal state. */
  watchedLive: boolean
  paused: boolean
  /** Updates buffered while paused. */
  pendingCount: number
  lastUpdatedAt: number | undefined
  errorMessage?: string
  pause(): void
  resume(): void
}

export function verifierBaseUrl(): string | undefined {
  const value = import.meta.env.VITE_WEB_TEST_API_BASE_URL
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

const POLL_INTERVAL_MS = 2_500
const SIMULATION_STEP_MS = 2_500
/** How many trailing fixture events the simulation reveals over time. */
const SIMULATION_TAIL = 3

function simulationHold(): boolean {
  try {
    return window.localStorage.getItem('wtd-simulate') === 'hold'
  } catch {
    return true
  }
}

interface FeedSnapshot {
  run: WebTestRun | undefined
  events: RunEvent[]
}

function fixtureSource(runId: string): { run: WebTestRun | undefined; events: RunEvent[] } {
  return {
    run: fixtureRuns.find((run) => run.id === runId),
    events: fixtureEvents
      .filter((event) => event.runId === runId)
      .sort((left, right) => left.sequence - right.sequence)
  }
}

/** How much of the fixture stream is visible before the simulation starts. */
function initialRevealCount(run: WebTestRun, events: RunEvent[]): number {
  if (run.state === 'terminal' || events.length === 0 || simulationHold()) return events.length
  return Math.max(1, events.length - SIMULATION_TAIL)
}

function initialFixtureSnapshot(runId: string): FeedSnapshot {
  const { run, events } = fixtureSource(runId)
  if (!run) return { run: undefined, events: [] }
  const slice = events.slice(0, initialRevealCount(run, events))
  return {
    run: slice.length === events.length ? run : applyEventsToRun(run, slice),
    events: slice
  }
}

export function useRunFeed(runId: string): RunFeed {
  const base = verifierBaseUrl()
  const mode: FeedMode = base === undefined ? 'fixture' : 'live'

  const [paused, setPaused] = useState(false)
  /** What the page renders; frozen while paused. */
  const [visible, setVisible] = useState<FeedSnapshot>(() =>
    mode === 'fixture' ? initialFixtureSnapshot(runId) : { run: undefined, events: [] }
  )
  /** The freshest known data; keeps advancing while paused. */
  const availableRef = useRef<FeedSnapshot>(visible)
  const pausedRef = useRef(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | undefined>(undefined)
  const [health, setHealth] = useState<
    'loading' | 'ok' | 'degraded' | 'unauthorized' | 'not-found'
  >(mode === 'fixture' ? 'ok' : 'loading')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const visibleCountRef = useRef(0)
  const watchedLiveRef = useRef(false)
  const publish = useCallback((snapshot: FeedSnapshot) => {
    availableRef.current = snapshot
    if (snapshot.run !== undefined && snapshot.run.state !== 'terminal') {
      watchedLiveRef.current = true
    }
    if (pausedRef.current) {
      const shown = visibleCountRef.current
      setPendingCount(Math.max(0, snapshot.events.length - shown))
    } else {
      visibleCountRef.current = snapshot.events.length
      setVisible(snapshot)
      setPendingCount(0)
      setLastUpdatedAt(Date.now())
    }
  }, [])

  const pause = useCallback(() => {
    pausedRef.current = true
    setPaused(true)
  }, [])
  const resume = useCallback(() => {
    pausedRef.current = false
    setPaused(false)
    const snapshot = availableRef.current
    visibleCountRef.current = snapshot.events.length
    setVisible(snapshot)
    setPendingCount(0)
    setLastUpdatedAt(Date.now())
  }, [])

  /* ---------------- fixture mode: simulated progression ---------------- */
  useEffect(() => {
    if (mode !== 'fixture') return
    const { run: baseRun, events: runEvents } = fixtureSource(runId)
    if (!baseRun) {
      publish({ run: undefined, events: [] })
      setHealth('not-found')
      return
    }
    setHealth('ok')

    // Reveal most of the stream immediately, then the tail over time — the
    // page looks alive without a long empty warm-up.
    let revealed = initialRevealCount(baseRun, runEvents)
    const emit = () => {
      const slice = runEvents.slice(0, revealed)
      publish({
        run: slice.length === runEvents.length ? baseRun : applyEventsToRun(baseRun, slice),
        events: slice
      })
    }
    emit()
    if (revealed >= runEvents.length) return
    const timer = window.setInterval(() => {
      if (revealed >= runEvents.length) {
        window.clearInterval(timer)
        return
      }
      revealed += 1
      emit()
    }, SIMULATION_STEP_MS)
    return () => window.clearInterval(timer)
  }, [mode, runId, publish])

  /* ---------------- live mode: resumable polling ---------------- */
  useEffect(() => {
    if (mode !== 'live' || base === undefined) return
    const api: DashboardApi = createDashboardApi(base)
    let disposed = false
    let timer: number | undefined

    const tick = async () => {
      const current = availableRef.current
      const afterSequence = current.events.at(-1)?.sequence ?? -1
      try {
        const [nextRun, nextEvents] = await Promise.all([
          api.getRun(runId),
          api.getEvents(runId, afterSequence)
        ])
        if (disposed) return
        const runs = mergeRunWithoutRegression(current.run ? [current.run] : [], nextRun)
        const mergedRun = runs.find((run) => run.id === runId) ?? nextRun
        publish({ run: mergedRun, events: mergeEvents(current.events, nextEvents) })
        setHealth('ok')
        setErrorMessage(undefined)
        if (mergedRun.state !== 'terminal') {
          timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS)
        }
      } catch (error) {
        if (disposed) return
        if (error instanceof DashboardApiError && (error.status === 401 || error.status === 403)) {
          setHealth('unauthorized')
          setErrorMessage(error.message)
          return
        }
        if (error instanceof DashboardApiError && error.status === 404) {
          setHealth('not-found')
          setErrorMessage(error.message)
          return
        }
        setHealth('degraded')
        setErrorMessage(error instanceof Error ? error.message : 'Live updates failed')
        timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS * 2)
      }
    }
    void tick()
    return () => {
      disposed = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [mode, base, runId, publish])

  const simulated =
    mode === 'fixture' && visible.run !== undefined && visible.run.state !== 'terminal'

  const liveness: FeedLiveness = useMemo(() => {
    if (health === 'loading') return 'loading'
    if (health === 'unauthorized') return 'unauthorized'
    if (health === 'not-found') return 'not-found'
    if (paused) return 'paused'
    if (health === 'degraded') return 'degraded'
    if (visible.run?.state === 'terminal') return 'finished'
    return 'live'
  }, [health, paused, visible.run?.state])

  return {
    mode,
    liveness,
    run: visible.run,
    events: visible.events,
    simulated,
    watchedLive: watchedLiveRef.current,
    paused,
    pendingCount,
    lastUpdatedAt,
    ...(errorMessage === undefined ? {} : { errorMessage }),
    pause,
    resume
  }
}
